/**
 * InlineGuide — Bloc d'aide contextuelle pour utilisateurs niveau "débutant".
 *
 * Affiche un encart sobre "Ici, vous pouvez…" + 3-5 puces concrètes + 1 action
 * suggérée. Visible uniquement si `manifest.show_inline_guides === true`
 * (niveau "Débutant" dans le PersonaSwitcher).
 *
 * Dismissable via localStorage (clé `noam.inline_guide.<id>.dismissed`).
 *
 * Usage :
 *   <InlineGuide
 *     id="annuaire-intro"
 *     title="Annuaire des entités"
 *     bullets={[
 *       "Recenser vos clients, partenaires et concurrents",
 *       "Tagger les <Super Partenaires> pour activer les analyses dédiées",
 *       "Consulter la fiche pour voir Fudswacca / Sales arguments",
 *     ]}
 *     hint="Conseil : commencez par 3-5 entités stratégiques."
 *   />
 */
import { useEffect, useState } from "react";
import { Lightbulb, X } from "lucide-react";
import { usePersona } from "@/lib/persona";

const STORAGE_PREFIX = "noam.inline_guide.";

export default function InlineGuide({ id, title, bullets = [], hint = null, icon: IconComp = Lightbulb }) {
  const { manifest } = usePersona();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!id) return;
    try {
      const v = localStorage.getItem(`${STORAGE_PREFIX}${id}.dismissed`);
      setDismissed(v === "1");
    } catch {
      /* noop */
    }
  }, [id]);

  if (!manifest?.show_inline_guides) return null;
  if (dismissed) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(`${STORAGE_PREFIX}${id}.dismissed`, "1");
    } catch {
      /* noop */
    }
    setDismissed(true);
  };

  return (
    <div
      data-testid={`inline-guide-${id}`}
      className="mb-4 p-3 rounded-lg border border-[#4A89DC]/30 bg-[#4A89DC]/5 relative"
    >
      <button
        type="button"
        onClick={dismiss}
        className="absolute top-2 right-2 text-zinc-500 hover:text-zinc-200 transition-colors"
        aria-label="Masquer ce guide"
        data-testid={`inline-guide-${id}-dismiss`}
      >
        <X className="w-3.5 h-3.5" />
      </button>

      <div className="flex items-start gap-2.5 pr-6">
        <IconComp className="w-4 h-4 text-[#4A89DC] flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          {title && (
            <div className="text-[12px] font-semibold text-[#4A89DC] mb-1.5">
              {title}
            </div>
          )}
          {bullets.length > 0 && (
            <ul className="space-y-1 text-[11px] text-zinc-300">
              {bullets.map((b, i) => (
                <li key={i} className="flex gap-1.5">
                  <span className="text-[#4A89DC] mt-0.5">·</span>
                  <span className="flex-1">{b}</span>
                </li>
              ))}
            </ul>
          )}
          {hint && (
            <div className="mt-2 text-[10.5px] text-zinc-400 italic border-l-2 border-[#4A89DC]/40 pl-2">
              {hint}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
